import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShoppingBag, Copy, Check, ExternalLink, RefreshCw, AlertTriangle } from "lucide-react";

const STEPS = [
  ["Open Google Merchant Center", "Sign in at merchants.google.com with the Google account that owns your business (free)."],
  ["Verify & claim picklebullpen.com", "Business info → Website: paste the site URL and follow the verification prompt (HTML tag or Google Analytics)."],
  ["Add the feed", "Products → Feeds → + → Country United States, language English → “Scheduled fetch” → paste the feed URL below → fetch daily."],
  ["Turn on free listings", "Growth → Manage programs → enable “Free product listings”. Items go live after review (usually 1–3 days)."],
  ["Add your return policy", "Business info → Shipping & returns → Return policies → paste https://www.picklebullpen.com/return-policy (30-day window, customer-paid returns). Google requires this for listings."],
];

export default function GoogleFeedPanel() {
  const [st, setSt] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);
  const load = () => api.get("/admin/feeds/google-shopping/status").then(({ data }) => setSt(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const copy = async () => {
    try { await navigator.clipboard.writeText(st.feed_url); setCopied(true); toast.success("Feed URL copied"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Copy failed — select the URL and copy it manually"); }
  };
  return (
    <div className="mt-6 border border-pb-border bg-pb-surface p-5" data-testid="google-feed-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2"><ShoppingBag className="w-4 h-4 text-[#4285F4]" /><span className="font-display font-bold uppercase">Google Shopping Feed — Free Listings</span></div>
        {st && (
          <span className="text-[10px] uppercase tracking-widest text-zinc-400" data-testid="google-feed-counts">
            <b className="text-white">{st.products}</b> products · <b className="text-white">{st.offers}</b> offers (size/color combos) · auto-updates from your catalog
          </span>
        )}
        <button data-testid="google-feed-refresh" onClick={load} className="w-7 h-7 flex items-center justify-center border border-pb-border hover:border-pb-red"><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        This live feed puts every Pro Shop item (with photos, price, sizes and colors) in front of shoppers searching Google — free. Paste the URL into Merchant Center once; it refreshes itself whenever you edit the catalog.
      </p>
      {st && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <input readOnly value={st.feed_url} data-testid="google-feed-url" onFocus={(e) => e.target.select()}
            className="flex-1 min-w-[260px] bg-black border border-pb-border px-3 py-2 font-mono text-xs text-[#A3E635] focus:outline-none" />
          <button data-testid="google-feed-copy" onClick={copy} className="flex items-center gap-1.5 bg-pb-red text-black px-3 py-2 text-[10px] font-black uppercase tracking-widest hover:bg-[#BEF264]">
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy URL"}
          </button>
          <a href={st.feed_url} target="_blank" rel="noopener noreferrer" data-testid="google-feed-open" className="flex items-center gap-1.5 border border-pb-border hover:border-white px-3 py-2 text-[10px] font-black uppercase tracking-widest text-zinc-300">
            <ExternalLink className="w-3.5 h-3.5" /> Preview feed
          </a>
          <a href="https://merchants.google.com/" target="_blank" rel="noopener noreferrer" data-testid="google-feed-mc" className="flex items-center gap-1.5 border border-[#4285F4]/60 text-[#8AB4F8] hover:bg-[#4285F4]/10 px-3 py-2 text-[10px] font-black uppercase tracking-widest">
            <ExternalLink className="w-3.5 h-3.5" /> Merchant Center
          </a>
        </div>
      )}
      <ol className="grid sm:grid-cols-2 gap-2 mb-3">
        {STEPS.map(([t, d], i) => (
          <li key={i} className="border border-pb-border/60 bg-black/30 p-3 flex gap-3" data-testid={`google-feed-step-${i + 1}`}>
            <span className="font-display font-black text-xl text-[#4285F4] leading-none">{i + 1}</span>
            <div><div className="text-xs font-bold text-white">{t}</div><div className="text-[11px] text-zinc-500 mt-0.5">{d}</div></div>
          </li>
        ))}
      </ol>
      {st?.skipped?.length > 0 && (
        <div className="text-[11px]" data-testid="google-feed-skipped">
          <button onClick={() => setShowSkipped(!showSkipped)} className="flex items-center gap-1.5 text-amber-400 hover:text-amber-300">
            <AlertTriangle className="w-3.5 h-3.5" /> {st.skipped.length} item{st.skipped.length === 1 ? "" : "s"} not in the feed {showSkipped ? "▲" : "▼"}
          </button>
          {showSkipped && (
            <ul className="mt-1.5 space-y-0.5 text-zinc-400">
              {st.skipped.map((s) => <li key={s.id}><span className="text-white">{s.name}</span> — {s.reason}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
